import { helpers, maxLength, minLength, required, sameAs } from '@vuelidate/validators';
import { urlPattern } from '@/utils';
import type { Validation, ValidationRuleWithoutParams, ValidationRuleWithParams } from '@vuelidate/core';

export const getErrors = (
  val: {
    $dirty: boolean;
    required?: { $invalid: boolean };
    minLength?: { $invalid: boolean };
    maxLength?: { $invalid: boolean };
    email?: { $invalid: boolean };
    checkUrl?: { $invalid: boolean };
    sameAs?: { $invalid: boolean };
  },
  specialMessages?: {
    required?: string;
    minLength?: string;
    maxLength?: string;
    sameAs?: string;
  },
): string[] => {
  if (!val.$dirty) {
    return [];
  }
  const errors: string[] = [];
  if (val.required?.$invalid) {
    errors.push(specialMessages?.required || 'This field is required');
  }
  if (val.minLength?.$invalid) {
    const min = (val.minLength as unknown as { $params: { min: string } }).$params.min;
    errors.push(specialMessages?.minLength || `The field should have a minimum of ${min} characters`);
  }
  if (val.checkUrl?.$invalid) {
    const max = (val.maxLength as unknown as { $params: { max: number } }).$params.max;
    errors.push(`This field must be maximum ${max} characters long`);
  }
  if (val.email?.$invalid) {
    errors.push('Wrong Email Format');
  }
  if (val.checkUrl?.$invalid) {
    errors.push(specialMessages?.maxLength || 'This field must be valid url');
  }
  if (val.sameAs?.$invalid) {
    const equalTo = (val.sameAs as unknown as { $params: { equalTo: string } }).$params.equalTo;
    errors.push(specialMessages?.sameAs || `The value must be ${equalTo}`);
  }
  return errors;
};

export const unsecuredPasswordHint = 'At least 4 characters';
export const passwordHint =
  'At least 8 characters, at least 1 lowercase letter, uppercase letter, number and special character';

// unused
//export type PasswordValidationType = Validation & {
//   minLength: Validation;
//   required: boolean;
//   maxLength: Validation;
//   validCharacter: Validation;
//   containsNumber: Validation;
//   containsLowerCase: Validation;
//   containsUpperCase: Validation;
//   containsSpecialCharacter: Validation;
// };
export type ConfirmPasswordValidationType = {
  sameAs: ValidationRuleWithParams;
  required: ValidationRuleWithoutParams;
};
// unused
//export type UnsecurePasswordValidationType = Validation & {
//   minLength: Validation;
//   required: boolean;
// };

export const validCharacter = helpers.regex(/^[a-zA-Z0-9~!?@#$%^&*_\-+()[\]{}<>/\\|"'.,]*$/);
export const containsSpecialCharacter = helpers.regex(/[~!?@#$%^&*_\-+()[\]{}<>/\\|"'.,]/);
export const containsNumber = helpers.regex(/\d/);
export const containsLowerCase = helpers.regex(/[a-z]/);
export const containsUpperCase = helpers.regex(/[A-Z]/);
export const validPhoneNumber = helpers.regex(/^\+[0-9]{7,15}$/);

export const unsecurePasswordValidators = {
  required,
  minLength: minLength(4),
};

export const passwordValidators = {
  required,
  minLength: minLength(8),
  maxLength: maxLength(64),
  validCharacter,
  containsNumber,
  containsLowerCase,
  containsUpperCase,
  containsSpecialCharacter,
};

export const phoneNumberValidators = {
  required,
  validPhoneNumber,
};

export const getConfirmPasswordValidators = (password: unknown): ConfirmPasswordValidationType => ({
  sameAs: sameAs(password),
  required,
});

export const getUnsecurePasswordValidationErrors = (password: Validation): string[] => {
  if (!password.$dirty) {
    return [];
  }
  const errors: string[] = [];
  if (password.minLength?.$invalid) {
    errors.push('Password field requires minimum of 4 characters');
  }
  if (password.required?.$invalid) {
    errors.push('This field is required');
  }
  return errors;
};

export const getPasswordValidationErrors = (password: Validation): string[] => {
  const errors: string[] = [];
  if (!password.$dirty) {
    return [];
  }
  if (password.required?.$invalid) {
    errors.push('Password is required');
  }
  if (password.minLength?.$invalid) {
    errors.push('Password field requires minimum of 8 characters');
  }
  if (password.maxLength?.$invalid) {
    errors.push('The password field must contain a maximum of 64 characters.');
  }
  if (password.validCharacter?.$invalid) {
    errors.push('Password contains invalid characters');
  }
  if (password.containsNumber?.$invalid) {
    errors.push('Password must contain at least one number');
  }
  if (password.containsLowerCase?.$invalid) {
    errors.push('Password must contain at least one lowercase letter');
  }
  if (password.containsUpperCase?.$invalid) {
    errors.push('Password must contain at least one uppercase letter');
  }
  if (password.containsSpecialCharacter?.$invalid) {
    errors.push('Password must contain at least one special character: ~!?@#$%^&*_-+()[]{}<>/\\|"\'.');
  }
  return errors;
};

export const getPhoneNumberValidationErrors = (phoneNumber: Validation): string[] => {
  if (!phoneNumber.$dirty) {
    return [];
  }
  const errors: string[] = [];
  if (phoneNumber.validPhoneNumber?.$invalid) {
    errors.push('Phone number must be in the format +123456789012345');
  }
  if (phoneNumber.required?.$invalid) {
    errors.push('This field is required');
  }
  return errors;
};

export const getConfirmPasswordValidationErrors = (confirmPassword: Validation, password: Validation): string[] => {
  if (password?.$invalid) {
    return [];
  }
  const errors: string[] = [];
  if (confirmPassword.sameAs?.$invalid) {
    errors.push("Passwords don't match");
  }
  if (confirmPassword.required?.$invalid) {
    errors.push('This field is required');
  }
  return errors;
};

export const delayValidation = (touchMap: WeakMap<WeakKey, number>, $v_: unknown): void => {
  const $v = $v_ as Validation;
  $v.$reset();
  if (touchMap.has($v)) {
    clearTimeout(touchMap.get($v));
  }
  touchMap.set($v, setTimeout($v.$touch, 800) as unknown as number);
};

export const checkUrl = (value: string): boolean => urlPattern.test(value);

// Just for old case where we don't use vuelidate
export const required_ = (value: string): boolean | string => Boolean(value) || 'This field is required';
